import { cn } from "@/modules/lib/utils"
import backgroundBenchmark from "@images/backgroundBenchmark.png"
import backgroundProfile from "@images/backgroundProfile.png"
import { ExploreCardFooterAvatar } from "@modules/explore/components/exploreCard/exploreCardFooterAvatar"
import { ExploreCardFooterChrono } from "@modules/explore/components/exploreCard/exploreCardFooterChrono"
import { ExploreCardHeader } from "@modules/explore/components/exploreCard/exploreCardHeader"
import { ExploreCardTitle } from "@modules/explore/components/exploreCard/exploreCardTitle"
import {
  ExploreBenchmarkItemEntity,
  ExploreBoxItemEntity,
  ExploreProfileItemEntity,
  ExploreProgramBundleItemEntity,
  ExploreProgramItemEntity,
  ExploreProProgramItemEntity,
} from "@modules/explore/entities/exploreEntity"
import { ExploreFilter } from "@modules/explore/exploreTypes"
import { type HUImage } from "@modules/types/imageTypes"
import { HUBackgroundImageView } from "@modules/ui/components/huBackgroundImageView"
import { useMemo } from "react"
type ExploreCardProps = {
  item:
    | ExploreBenchmarkItemEntity
    | ExploreProgramItemEntity
    | ExploreProgramBundleItemEntity
    | ExploreProProgramItemEntity
    | ExploreBoxItemEntity
    | ExploreProfileItemEntity
  onViewDetailsClick: (itemId: string, typeOf: ExploreFilter) => void
  isSelected: boolean
  backgroundImage?: string
  disabled?: boolean
  className?: string
  size?: string
} & React.ComponentProps<"div">

export function ExploreCard({
  item,
  onViewDetailsClick,
  isSelected,
  backgroundImage = "",
  disabled = false,
  className,
  size,
  ...props
}: ExploreCardProps) {
  const itemImage = useMemo(() => {
    if (backgroundImage !== "") {
      return { url: backgroundImage }
    }

    switch (item._type) {
      case ExploreFilter.PROGRAM:
      case ExploreFilter.PROGRAM_BUNDLE:
      case ExploreFilter.PROGRAM_PRO:
        return item.image
      case ExploreFilter.BOX:
        return item.cover
      case ExploreFilter.BENCHMARK:
        return { url: backgroundBenchmark }
      case ExploreFilter.PROFILE:
        return { url: backgroundProfile }
    }
  }, [item, backgroundImage])

  return (
    <HUBackgroundImageView
      image={itemImage as HUImage}
      onClick={() => {
        if (item._type && !disabled) {
          onViewDetailsClick(item.id, item._type)
        }
      }}
      className={cn(
        "group relative flex h-75 w-87 flex-col justify-between overflow-hidden rounded-4xl bg-cover bg-center bg-no-repeat",
        {
          "cursor-pointer": !disabled,
          "bg-neutral-100": item._type === ExploreFilter.PROFILE,
          "ring-2 ring-borderDefault": isSelected,
        },
        className,
        size,
      )}
      size={size ?? "h-75 w-87"}
      {...props}
    >
      <ExploreCardHeader item={item} />
      <div className="z-2 flex flex-col pr-3 pb-4 pl-4">
        {item._type !== ExploreFilter.PROFILE && <ExploreCardTitle item={item} />}
        {item._type !== ExploreFilter.BENCHMARK ? (
          <ExploreCardFooterAvatar item={item} />
        ) : (
          <ExploreCardFooterChrono item={item} />
        )}
      </div>

      <div
        className={cn("absolute top-0 right-0 bottom-0 left-0 z-1 h-full w-full rounded-4xl bg-linear-to-b", {
          "from-[rgba(34,34,34,0)] to-[rgba(34,34,34,0.8)]":
            item._type === ExploreFilter.PROGRAM ||
            item._type === ExploreFilter.PROGRAM_BUNDLE ||
            item._type === ExploreFilter.PROGRAM_PRO ||
            item._type === ExploreFilter.BOX,
          "group-hover:from-[rgba(19,60,64,0)] group-hover:to-[#133C40]":
            !disabled &&
            (item._type === ExploreFilter.PROGRAM ||
              item._type === ExploreFilter.PROGRAM_BUNDLE ||
              item._type === ExploreFilter.PROGRAM_PRO ||
              item._type === ExploreFilter.BOX),
          "group-hover:bg-[linear-gradient(180deg,rgba(255,255,255,0)_0%,rgba(221,242,71,0)_0.01%,rgba(221,242,71,0.76)_99.9%)] group-hover:backdrop-blur-[2px]":
            item._type === ExploreFilter.BENCHMARK && !disabled,
          "group-hover:bg-[linear-gradient(180deg,rgba(255,255,255,0)_0%,rgba(230,230,230,0)_0.01%,rgba(230,230,230,0.76)_99.9%)] group-hover:backdrop-blur-[2px]":
            item._type === ExploreFilter.PROFILE && !disabled,
        })}
      ></div>
    </HUBackgroundImageView>
  )
}
